import React, { useCallback, useEffect, useRef } from 'react'
import { useModalTranslation } from '../../i18n/useModalTranslation'
import TailwindModal from '../ui/TailwindModal'
import type { AppLayoutMode } from '../../contexts/appViewportContext'

interface LandPageModalProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  width?: string
  maxWidth?: string
  /** When set, modal shell uses this fixed height (and scrolls inside content). */
  height?: string
  minHeight?: string
  isLoading?: boolean
  cornerColors?: {
    topLeft?: string
    topRight?: string
    bottomLeft?: string
    bottomRight?: string
  }
  closeButtonColor?: string
  accentColor?: string
  /** Optional custom background (e.g. gradient). When set, used for the main panel instead of default. */
  background?: string
  /**
   * Full-bleed image behind modal content only. Use with `background="transparent"` (or omit `background`)
   * and optional `wallpaperImageFilter` (e.g. brightness(0.88)) so only the art is dimmed, not UI text.
   */
  wallpaperImageUrl?: string
  /** CSS `filter` applied only to the wallpaper layer (default none). */
  wallpaperImageFilter?: string
  /** When true, corner accents and panel border are hidden. */
  hideCornerBorders?: boolean
  /** When true, the main panel border is hidden. */
  hideBorder?: boolean
  /** Optional data-ftue-anchor on the default close button (FTUE spotlight target). */
  closeButtonFtueAnchor?: string
  /** When true, the default close button is not rendered (caller provides its own). */
  hideCloseButton?: boolean
  /** When true, content area has no scroll (overflow hidden). Use for compact modals that fit in viewport. */
  contentNoScroll?: boolean
  disableEscapeKeyDown?: boolean
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  /** Extra classes on the outer positioned container (e.g. responsive max-width). */
  containerClassName?: string
  /** Extra classes on the main panel (e.g. rounded-none for full-bleed artwork). */
  panelClassName?: string
  /**
   * Header title plate straddling the panel's top edge (half above). `logoSrc`
   * is the banner image used as the plate's background; `logoTitle` is the title
   * text drawn on it. When set, the outer container stops clipping so the plate
   * can overflow upward.
   */
  logoSrc?: string
  logoAlt?: string
  logoTitle?: string
  /** Portal overlay z-index (default matches TailwindModal). */
  zIndex?: number
  /** Square corners and no default rounding — use with full-bleed background art. */
  squarePanel?: boolean
  /** Smaller top-right close control (e.g. compact PvP dialogs). */
  closeButtonSize?: 'default' | 'compact'
  /**
   * Letterboxed art modals: inner `.fit` / panel use `pointer-events: none` and `.root` uses `pointer-events: auto`.
   * The shell captures wheel/drag on letterbox so main land does not move; clicks on the shell dismiss the modal.
   */
  passBackdropPointerEvents?: boolean
  /** When set with `closeButtonSize="compact"`, phone landscape uses a smaller circular close control. */
  layoutMode?: AppLayoutMode
  /** Optional extra classes for the close button wrapper. */
  closeButtonClassName?: string
  /**
   * Lets content overflow the outer shell uncropped — same effect `logoSrc` has on the container,
   * split out for callers that build their own straddling title plate instead of using `logoSrc`.
   */
  overflowVisible?: boolean
  /**
   * Rendered as a sibling *before* the scrollable content area, inside the panel but outside the
   * `overflow-y-auto` wrapper that clips `children` — use for a custom straddling title plate (built
   * with `overflowVisible`) that needs to overlap the panel's top edge; the scroll wrapper would
   * otherwise crop it even with `overflowVisible` set, since that wrapper clips independently.
   */
  headerOverlay?: React.ReactNode
  /** Optional extra classes for the close icon. */
  closeButtonIconClassName?: string
}

const LandPageModal: React.FC<LandPageModalProps> = ({
  open,
  onClose,
  children,
  width = '95vw',
  maxWidth = '900px',
  height,
  minHeight,
  isLoading = false,
  cornerColors = {
    topLeft: 'rgba(107, 114, 128, 0.5)',
    topRight: 'rgba(75, 85, 99, 0.5)',
    bottomLeft: 'rgba(55, 65, 81, 0.5)',
    bottomRight: 'rgba(107, 114, 128, 0.5)',
  },
  closeButtonColor = 'rgba(107, 114, 128, 0.4)',
  accentColor = 'rgba(107, 114, 128, 0.2)',
  background,
  wallpaperImageUrl,
  wallpaperImageFilter,
  hideCornerBorders = false,
  hideBorder = false,
  hideCloseButton = false,
  closeButtonFtueAnchor,
  contentNoScroll = false,
  disableEscapeKeyDown = false,
  ariaLabelledBy,
  ariaDescribedBy,
  containerClassName,
  panelClassName,
  logoSrc,
  logoAlt,
  logoTitle,
  zIndex,
  squarePanel = false,
  closeButtonSize = 'default',
  layoutMode = 'desktop',
  closeButtonClassName,
  closeButtonIconClassName,
  passBackdropPointerEvents = false,
  overflowVisible = false,
  headerOverlay,
}) => {
  const { t } = useModalTranslation()
  const scrollContentRef = useRef<HTMLDivElement>(null)

  /* Lock body scroll when modal is open so background doesn't move on mobile */
  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  /* On mobile: prevent touch on backdrop from scrolling background; allow touch only on scrollable content */
  const handleModalTouchMove = (e: React.TouchEvent) => {
    const scrollEl = scrollContentRef.current
    if (scrollEl && scrollEl.contains(e.target as Node)) return
    e.preventDefault()
  }

  const blockLandPointerBubble = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  const handlePassBackdropShellClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  const handlePassBackdropShellWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const scrollEl = scrollContentRef.current
    if (scrollEl?.contains(e.target as Node)) {
      e.stopPropagation()
      return
    }
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const panelBackground =
    background ??
    (wallpaperImageUrl
      ? 'transparent'
      : `
              radial-gradient(ellipse 150% 120% at 0% 0%, rgba(107, 114, 128, 0.25), transparent 70%),
              radial-gradient(ellipse 150% 120% at 100% 100%, rgba(75, 85, 99, 0.2), transparent 70%),
              radial-gradient(circle at 50% 50%, rgba(31, 41, 55, 0.3), transparent),
              linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(31, 41, 55, 0.9) 50%, rgba(17, 24, 39, 0.95) 100%)
            `)

  const compactCloseLandscape =
    closeButtonSize === 'compact' && layoutMode === 'mobile-landscape'

  const closeBtnPositionClass =
    closeButtonSize === 'compact'
      ? compactCloseLandscape
        ? 'top-2 right-2'
        : 'top-2 right-2 sm:top-3 sm:right-3'
      : 'top-2 right-2 sm:top-4 sm:right-4'
  const closeBtnBoxClass =
    closeButtonSize === 'compact'
      ? compactCloseLandscape
        ? 'h-5 w-5 shrink-0 rounded-full'
        : 'h-6 w-6 rounded-md sm:h-7 sm:w-7'
      : 'h-8 w-8 rounded-xl sm:h-10 sm:w-10'
  const closeBtnIconClass =
    closeButtonSize === 'compact'
      ? compactCloseLandscape
        ? 'text-[10px] leading-none group-hover:rotate-90 transition-transform duration-300'
        : 'text-xs sm:text-sm group-hover:rotate-90 transition-transform duration-300'
      : 'text-lg group-hover:rotate-90 transition-transform duration-300'

  const style = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width,
    maxWidth,
    ...(height
      ? { height, maxHeight: height }
      : { maxHeight: 'min(96dvh, 95vh)' }),
    outline: 'none',
    backgroundColor: 'transparent',
    // Let the header logo overflow above the panel; otherwise clip as before.
    overflow: logoSrc || overflowVisible ? 'visible' : 'hidden',
  }

  return (
    <TailwindModal
      open={open}
      onClose={onClose}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      disableEscapeKeyDown={disableEscapeKeyDown || isLoading}
      zIndex={zIndex}
      /* Smooth enter/exit — see LandPageModal.css. exitMs holds the tree
         mounted so the exit animation can play instead of hard-cutting. */
      exitMs={140}
      backdropClassName="landpage-modal-backdrop"
      slotProps={{
        backdrop: {
          onTouchMove: (e) => e.preventDefault(),
          style: { touchAction: 'none', overscrollBehavior: 'none' },
        },
      }}
    >
      <div
        className={containerClassName}
        style={{
          ...style,
          ...(passBackdropPointerEvents ? { pointerEvents: 'auto' as const, touchAction: 'none' } : {}),
        }}
        onTouchMove={passBackdropPointerEvents ? undefined : handleModalTouchMove}
        {...(passBackdropPointerEvents
          ? {
              onClick: handlePassBackdropShellClick,
              onWheel: handlePassBackdropShellWheel,
              onTouchMove: blockLandPointerBubble,
              onTouchMoveCapture: blockLandPointerBubble,
              onMouseDown: blockLandPointerBubble,
              onMouseDownCapture: blockLandPointerBubble,
            }
          : {})}
      >
        {logoSrc && (
          <div
            className="upgrade-restore-logo"
            style={{ backgroundImage: `url(${logoSrc})` }}
            role="img"
            aria-label={logoTitle ?? logoAlt ?? ''}
          >
            {logoTitle && (
              /* id wires the dialog's aria-labelledby to the visible title. */
              <span className="upgrade-restore-logo-text" id={ariaLabelledBy}>
                {logoTitle}
              </span>
            )}
          </div>
        )}
        <div
          className={`landpage-panel relative flex w-full min-h-0 flex-col ${height ? 'h-full max-h-full' : ''} ${hideBorder ? '' : 'backdrop-blur-sm'} rounded-2xl ${panelClassName ?? ''}`}
          style={{
            background: panelBackground,
            border: hideBorder ? 'none' : `2px solid ${background || wallpaperImageUrl ? accentColor : 'rgba(107, 114, 128, 0.4)'}`,
            boxShadow: hideBorder
              ? `none`
              : `
                0 20px 60px rgba(0, 0, 0, 0.6),
                0 0 0 1px ${accentColor},
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                inset 0 -1px 0 rgba(0, 0, 0, 0.3)
              `,
            minHeight,
            maxHeight: height ? '100%' : 'min(96dvh, 95vh)',
            ...(height ? { height: '100%' } : {}),
            ...(passBackdropPointerEvents ? { pointerEvents: 'none' as const } : {}),
          }}
        >
          {wallpaperImageUrl ? (
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-0 z-0 ${squarePanel ? 'rounded-none' : 'rounded-2xl'}`}
              style={{
                backgroundImage: `url(${wallpaperImageUrl})`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                ...(wallpaperImageFilter ? { filter: wallpaperImageFilter } : {}),
              }}
            />
          ) : null}
          {!hideCornerBorders && (
            <>
              <div
                className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-t-2 border-l-2 rounded-tl-2xl"
                style={{ borderColor: cornerColors.topLeft }}
              />
              <div
                className="absolute top-0 right-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-t-2 border-r-2 rounded-tr-2xl"
                style={{ borderColor: cornerColors.topRight }}
              />
              <div
                className="absolute bottom-0 left-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-b-2 border-l-2 rounded-bl-2xl"
                style={{ borderColor: cornerColors.bottomLeft }}
              />
              <div
                className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 border-b-2 border-r-2 rounded-br-2xl"
                style={{ borderColor: cornerColors.bottomRight }}
              />
            </>
          )}

          {!hideCloseButton && (
            <button
              onClick={onClose}
              disabled={isLoading}
              type="button"
              data-ftue-anchor={closeButtonFtueAnchor}
              aria-label={t('modals.landPageModal.close')}
              className={`absolute z-50 flex items-center justify-center text-white transition-all group ${closeBtnPositionClass} ${closeBtnBoxClass} ${closeButtonClassName ?? ''} ${
                isLoading ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:scale-110 active:scale-95'
              }`}
              style={{
                fontFamily: 'Tilt Warp',
                background: `linear-gradient(135deg, ${closeButtonColor.replace('0.4', '0.2')}, ${closeButtonColor.replace('0.4', '0.15')})`,
                backdropFilter: 'blur(12px)',
                border: `1px solid ${closeButtonColor}`,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              }}
            >
              <span className={`${closeBtnIconClass} ${closeButtonIconClassName ?? ''}`}>✕</span>
            </button>
          )}

          {/* Loading Overlay */}
          {isLoading && (
            <div
              className={`absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm ${
                squarePanel ? 'rounded-none' : 'rounded-xl'
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 border-[3px] border-t-transparent rounded-full animate-spin"
                  style={{
                    borderColor: `${closeButtonColor.replace('0.4', '0.5')} ${closeButtonColor.replace('0.4', '0.2')} ${closeButtonColor.replace('0.4', '0.2')} ${closeButtonColor.replace('0.4', '0.2')}`,
                  }}
                ></div>
                <p
                  className="text-white text-sm mt-4 font-medium"
                  style={{
                    fontFamily: 'Tilt Warp',
                    color: 'rgba(255, 255, 255, 0.9)',
                  }}
                >
                  {t('modals.landPageModal.processing')}
                </p>
              </div>
            </div>
          )}

          {headerOverlay}

          {/* Content: flex-1 + min-h-0 so nested modals can own scroll when contentNoScroll */}
          <div
            ref={scrollContentRef}
            className={`${
              contentNoScroll
                ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                : 'modal-scrollable-content min-h-0 w-full flex-1 overflow-y-auto overscroll-y-contain'
            }`}
            style={
              contentNoScroll
                ? { touchAction: 'pan-y', overscrollBehavior: 'none' }
                : {
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(107, 114, 128, 0.6) rgba(17, 24, 39, 0.3)',
                    touchAction: 'pan-y',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                  }
            }
          >
            {!contentNoScroll && (
              <style>{`
                .modal-scrollable-content::-webkit-scrollbar {
                  width: 8px;
                }
                .modal-scrollable-content::-webkit-scrollbar-track {
                  background: rgba(17, 24, 39, 0.3);
                  border-radius: 4px;
                }
                .modal-scrollable-content::-webkit-scrollbar-thumb {
                  background: rgba(107, 114, 128, 0.6);
                  border-radius: 4px;
                }
                .modal-scrollable-content::-webkit-scrollbar-thumb:hover {
                  background: rgba(107, 114, 128, 0.8);
                }
              `}</style>
            )}
            {children}
          </div>
        </div>
      </div>
    </TailwindModal>
  )
}

export default LandPageModal

